import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";

import { resObj } from "../../utils/constants";
import useAxios from "../../utils/useAxios";
import PermissionContext from "../../context/PermissionContext";
import DrawerContext from "../../context/DrawerContext";
import AutoLogout from "../../utils/useAutoLogout";
import { 
  parameterHeading, ribogreenHeading, dlsSummaryHeading, transfectionHeading,
  endosomalRuptureHeading, biodistributionInVivoHeading, biodistributionExVivoHeading,
  dlsHeading, dlsRunHeading, lalHeading, cytotoxicityHeading, ku812Heading, 
  coagulationHeading, complementHeading, coagulationMetricsHeading,
  mRNAExpressionExVivoWholeTissueHeading, mRNAExpressionExVivoCellTypeSpecificHeading,
  mechanismOfInternalizationHeading, mechanismOfInternalizationMetricsHeading,
  potencyTEHeading, potencyTEMetricsHeading, parameterIgniteHeading,
  zetaPotentialHeading, zetaPotentialRunHeading, siRNAtransfectionHeading,
} from './tableHeading';

import Typography from "@mui/material/Typography";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Box from "@mui/material/Box";

import SideDrawer from "../../components/layout/SideDrawer";
import LNPSampleHeader from "../../components/analytics/LNPSampleHeader";
import Loader from "../../components/layout/Loader";
import ErrorModal from "../../components/layout/ErrorModal";
import FormulationForm from "../../components/analytics/FormulationForm";
import SubTable from "../../components/layout/SubTable";
import CommentModalForm from "../../components/analytics/CommentModalForm";
import SuccessAlert from "../../components/layout/SuccessAlert";
import CommentCard from "../../components/analytics/CommentCard";
import DeleteModal from "../../components/layout/DeleteModal";
import AnalysisTab from "../../components/analytics/AnalysisTab";
import NoData from "../../components/layout/NoData";

const LNPSampleDetailPage = () => {
  const api = useAxios();
  const navigate = useNavigate();

  const { permisionsList, checkUserPermission } = useContext(PermissionContext);
  const { drawerValue, setDrawerValue } = useContext(DrawerContext);

  useEffect(() => {
    if(permisionsList.length > 0) checkUserPermission(['Analytics-View Data']);
  }, [permisionsList]);

  const initialValues = {
    lnpSampleName: '',
    ionisableName: '',
    sterolName: '',
    neutralName: '',
    pegName: '',
    ionisableMolarRatio: '',
    sterolMolarRatio: '',
    neutralMolarRatio: '',
    pegMolarRatio: '',
    lipidTotalMolarConcentration: '',
    npRatio: '',
    ilpRatio: '',
    robotOperator: '',
    payloadName: '',
    lipidsList: [],
    parameterList: [],
    parameterIgniteList: [],
  };
  const [apiRes, setApiRes] = useState(resObj);
  const [values, setValues] = useState(initialValues);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [comment, setComment] = useState('');
  const [commentList, setCommentList] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteModalObj, setDeleteModalObj] = useState({
    title: '', message: '', type: ''
  });
  const [commentId, setCommentId] = useState('');
  const [tabValue, setTabValue] = useState('CPI');
  const [assayTabValue, setAssayTabValue] = useState('');
  const [assayTabList, setAssayTabList] = useState([]);
  const [assayTableValues, setAssayTableValues] = useState([]);
  const [showNoData, setShowNoData] = useState(true);
  
  const docUrIArr = window.location.href.split('/');
  const lnpSampleId = docUrIArr[docUrIArr.length - 1];
  const backURL = '/formulations';
  const lnpDeleteObj = {
    title: 'LNP Sample Delete',
    message: `Are you sure you want to delete? 
    (All related informations, LNP Paramters, PSD Summary, PSD Graph, 
    Ribogreen, Transfection and Comments will be lost)`,
    type: 'LNP Sample',
  };
  const commentDeleteObj = {
    title: 'Comment Delete',
    message: `Are you sure you want to delete (Comment will be lost)?`,
    type: 'Comment',
  };

  const replicateArray = (arr, replicate) => {
    return Array.from(
      { length: replicate }, (_, i) => arr.map(item => `${item} ${(i + 1)}`)
    ).flat();
  };

  const cpiData = [
    {
      tabName: 'DLS', 
      name: 'PSD Summary', 
      data: [], 
      heading: dlsSummaryHeading
    },
    {
      tabName: 'DLS', 
      name: 'PSD Graph', 
      data: [], 
      heading: ['Radius (n/m)']
    },
    {
      tabName: 'Ribogreen', 
      name: 'Ribogreen', 
      data: [], 
      heading: ribogreenHeading
    },
  ];
  const [cpiTabData, setCpiTabData] = useState(cpiData);

  const uosData = [
    {
      tabName: 'DLS', 
      name: 'DLS', 
      data: [], 
      heading: dlsHeading,
      excludeKeys: ['dls_id', 'run']
    },
    {
      tabName: 'Ribogreen', 
      name: 'Ribogreen', 
      data: [], 
      heading: ribogreenHeading
    },
    {
      tabName: 'Zeta Potential', 
      name: 'Zeta Potential', 
      data: [], 
      heading: zetaPotentialHeading,
      excludeKeys: ['zeta_potential_id', 'run']
    },
  ];
  const [uosTabData, setUosTabData] = useState(uosData);

  const mdcData = [
    {
      tabName: 'Transfection', 
      name: 'Transfection', 
      data: [], 
      heading: transfectionHeading
    },
    {
      tabName: 'siRNA Transfection', 
      name: 'siRNA Transfection', 
      data: [], 
      heading: siRNAtransfectionHeading
    },
    {
      tabName: 'Potency TE', 
      name: 'Potency Transfection Efficiency', 
      data: [], 
      heading: potencyTEHeading,
      excludeKeys: [
        'potency_transfection_efficiency_id', 'technical_replicate'
      ]
    },
    {
      tabName:'Endosomal Rupture', 
      name: 'Endosomal Rupture', 
      data: [], 
      heading: endosomalRuptureHeading,
      excludeKeys: ['endosomal_rupture_id', 'technical_replicate']
    },
    {
      tabName: 'Biodistribution In Vivo', 
      name: 'Biodistribution In Vivo', 
      data: [], 
      heading: biodistributionInVivoHeading,
      excludeKeys: ['lnp_information_id']
    },
    {
      tabName: 'Biodistribution Ex Vivo', 
      name: 'Biodistribution Ex Vivo', 
      data: [], 
      heading: biodistributionExVivoHeading,
      excludeKeys: ['lnp_information_id']
    },
    {
      tabName: 'LNP Efficacy in Ex Vivo', 
      name: 'LNP Efficacy in Ex Vivo - Whole Tissue', 
      data: [], 
      heading: mRNAExpressionExVivoWholeTissueHeading,
      excludeKeys: ['lnp_information_id']
    },
    {
      tabName: 'LNP Efficacy in Ex Vivo',
      name: 'LNP Efficacy in Ex Vivo - Cell Type Specific', 
      data: [], 
      heading: mRNAExpressionExVivoCellTypeSpecificHeading,
      excludeKeys: ['lnp_information_id']
    },
    {
      tabName: 'Mechanism of Internalization',
      name: 'Mechanism of Internalization', 
      data: [], 
      heading: mRNAExpressionExVivoCellTypeSpecificHeading,
      excludeKeys: ['mechanism_of_internalization_id', 'technical_replicate']
    },
  ];
  const [mdcTabData, setMdcTabData] = useState(mdcData);
  
  const uolData = [
    {
      tabName: 'DLS', 
      name: 'DLS', 
      data: [], 
      heading: dlsHeading,
      excludeKeys: ['dls_id', 'run']
    },
    {tabName: 'LAL', name: 'LAL', data: [], heading: lalHeading},
    {
      tabName: 'Cytotoxicity', 
      name: 'Cytotoxicity', 
      data: [], 
      heading: cytotoxicityHeading
    },
    {tabName: 'KU812', name: 'KU812', data: [], heading: ku812Heading},
    {
      tabName: 'Complement',
      name: 'Complement', 
      data: [], 
      heading: complementHeading, 
      excludeKeys: ['complement_id', 'replicate', 'date_tested_2', 'date_tested_4'],
    },
    {
      tabName: 'Coagulation',
      name: 'Coagulation', 
      data: [], 
      heading: coagulationHeading, 
      excludeKeys: ['coagulation_id', 'replicate']
    },
  ];
  const [uolTabData, setUolTabData] = useState(uolData);

  useEffect(() => {
    getLNPSampleDetails();
  }, []);

  const getLNPSampleDetails = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/analytics/lnp/samples/detail/get', {
        params: { 
          lnp_sample_id: lnpSampleId,
        }
      });
      
      if (response.status === 200) {
        const data = response.data;        
        setValues({
          ...values,
          lnpSampleName: data.lnp_sample.lnp_sample_name,
          ionisableName: data.lnp_sample.ionisable_name,
          sterolName: data.lnp_sample.sterol_name,
          neutralName: data.lnp_sample.neutral_name,
          pegName: data.lnp_sample.peg_name,
          ionisableMolarRatio: data.lnp_sample.ionisable_molar_ratio,
          sterolMolarRatio: data.lnp_sample.sterol_molar_ratio,
          neutralMolarRatio: data.lnp_sample.neutral_molar_ratio,
          pegMolarRatio: data.lnp_sample.peg_molar_ratio,
          lipidTotalMolarConcentration: data.lnp_sample.lipid_total_molar_concentration,
          npRatio: data.lnp_sample.np_ratio,
          ilpRatio: data.lnp_sample.ilp_ratio,
          robotOperator: data.lnp_sample.robot_operator,
          payloadName: data.lnp_sample.payload_name,
          lipidsList: data.lnp_sample.lipids_list,
          parameterList: data.lnp_parameter,
          parameterIgniteList: data.lnp_parameter_ignite,
        });

        getComments();
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'LNP Sample Details Get',
        loading: false,
      });
    }
  };

  const addComment = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const formData = new FormData();
      formData.append("comment", comment);
      formData.append("lnp_sample_id", lnpSampleId);
      const response = await api.post('/api/user/add/lnp/sample/comment', formData);

      if (response.status === 200) {
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: response.data,
          loading: false,
        });
        setComment('');
        getComments();
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Add Comment',
        loading: false,
      });
    }
  };

  const getComments = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get('/api/user/lnp/sample/comments/get', {
        params: { 
          lnp_sample_id: lnpSampleId,
        }
      });
      
      if (response.status === 200) {
        setCommentList(response.data);
        setApiRes({...apiRes, loading: false});
      }
    } catch (err){
      if (err.message === 'Request failed with status code 403') {
        setApiRes({...apiRes, loading: false});
      } else {
        setApiRes({
          ...apiRes,
          axiosError: true,
          errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
          errHeading: 'Comments Get',
          loading: false,
        });
      }
    }
  };

  const deleteLNPSample = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.delete(`/api/analytics/lnp/sample/delete/${lnpSampleId}`);

      if (response.status === 200) {
        setDeleteModalOpen(false);
        navigate('/formulations');
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: 'Sample deleted successfully',
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Sample Delete',
        loading: false,
      });
    }
  };

  const handleLnpSampleDelete = () => {
    setDeleteModalObj(lnpDeleteObj);
    setDeleteModalOpen(true);
  };

  const handleDeleteComment = commentId => { 
    setCommentId(commentId); 
    setDeleteModalObj(commentDeleteObj);
    setDeleteModalOpen(true);
  };

  const deleteComment = async () => {
    try {
      setApiRes({...apiRes, loading: true});
      setDeleteModalOpen(false);
      const response = await api.delete(`/api/user/lnp/sample/comment/delete/${commentId}`);

      if (response.status === 200) {
        getComments();
        setApiRes({
          ...apiRes,
          showAlert: true,
          successMsg: 'Comment deleted successfully',
          loading: false,
        });
      }
    } catch (err) {
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'Comment Delete',
        loading: false,
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    let url = '';
    if (tabValue === 'CPI') {
      url = '/api/analytics/cpi/analysis/result/get';
    } else if (tabValue === 'UoS') {
      url = '/api/partner/uos/analytics/analysis/result/get';
    } else if (tabValue === 'MDC') {
      url = '/api/partner/mdc/analytics/analysis/result/get';
    } else {
      url = '/api/partner/uol/analytics/analysis/result/get';
    }
    if (url !== '') getAnalysisData(url);
  }, [tabValue]);

  const getAnalysisData = async (url) => {
    try {
      setApiRes({...apiRes, loading: true});
      const response = await api.get(url, {
        params: { 
          lnp_sample_id: lnpSampleId,
        }
      });
      
      if (response.status === 200) {
        subTableDataUpdate(response.data);
        setApiRes({ ...apiRes, loading: false });
      }
    } catch (err){      
      setApiRes({
        ...apiRes,
        axiosError: true,
        errMsg: JSON.stringify(err.response.data).replace(/[{}[\]"']/g, ''),
        errHeading: 'LNP Sample Details Get',
        loading: false,
      });
    }
  };

  const subTableDataUpdate = (data) => {    
    let assayTabs = [];
    let defaultAssayTab = '';
    if (tabValue === 'CPI') {
      let cpiCopyData = [...cpiTabData];
      cpiCopyData = cpiCopyData.map((obj) =>
        obj.name === "Ribogreen" ? {...obj, data: data.ribogreen} : obj
      );
      cpiCopyData = cpiCopyData.map((obj) =>
        obj.name === "PSD Summary" ? {...obj,  data: data.dls_summary} : obj
      );
      cpiCopyData = cpiCopyData.map((obj) =>
        obj.name === "PSD Graph" ? 
        {
          ...obj, 
          heading: data.graph_title_list, 
          data: data.graph_data_list
        } 
        : obj
      );

      assayTabs = [...new Set(cpiCopyData.map((obj) => obj.tabName))];
      defaultAssayTab = assayTabs.length > 0 ? assayTabs[0] : '';
      setCpiTabData(cpiCopyData);
    }

    if (tabValue === 'UoS') {
      let uosCopyData = [...uosTabData];
      uosCopyData = uosCopyData.map((obj) =>
        obj.name === "Ribogreen" ? {...obj, data: data.ribogreen} : obj
      );
      uosCopyData = uosCopyData.map((obj) =>
        obj.name === "DLS" ? {...obj,  data: data.dls} : obj
      );

      const zpMaxReplicate = Math.max(
        ...data.zeta_potential.map(item => item.run)
      );
      const zpMetricsHeading = replicateArray(
        zetaPotentialRunHeading, zpMaxReplicate
      );
      uosCopyData = uosCopyData.map((obj) =>
        obj.name === "Zeta Potential" ? 
        {
          ...obj, 
          heading: zetaPotentialHeading.concat(zpMetricsHeading),
          data: data.zeta_potential,
        }
        : obj
      );

      assayTabs = [...new Set(uosCopyData.map((obj) => obj.tabName))];
      defaultAssayTab = assayTabs.length > 0 ? assayTabs[0] : '';
      setUosTabData(uosCopyData);
    }
    
    if (tabValue === 'MDC') {
      let mdcCopyData = [...mdcTabData];
      mdcCopyData = mdcCopyData.map((obj) =>
        obj.name === "Transfection" ? {...obj, data: data.transfection} : obj
      );

      mdcCopyData = mdcCopyData.map((obj) =>
        obj.name === "siRNA Transfection" ? {...obj, data: data.sirna_transfection} : obj
      );

      const pteMaxReplicate = Math.max(
        ...data.potency_te.map(item => item.technical_replicate)
      );
      const pteMetricsHeading = replicateArray(
        potencyTEMetricsHeading, pteMaxReplicate
      );
      mdcCopyData = mdcCopyData.map((obj) =>
        obj.name === "Potency Transfection Efficiency" ? 
          {
            ...obj, 
            heading: potencyTEHeading.concat(pteMetricsHeading), 
            data: data.potency_te
          } 
          : obj
      );

      mdcCopyData = mdcCopyData.map((obj) =>
        obj.name === "Endosomal Rupture" ? 
          {...obj, data: data.endosomal_rupture} : obj
      );
      mdcCopyData = mdcCopyData.map((obj) =>
        obj.name === "Biodistribution In Vivo" ? 
          {...obj, data: data.biodistribution_in_vivo} : obj
      );
      mdcCopyData = mdcCopyData.map((obj) =>
        obj.name === "Biodistribution Ex Vivo" ? 
          {...obj, data: data.biodistribution_ex_vivo} : obj
      );
      mdcCopyData = mdcCopyData.map((obj) =>
        obj.name === "LNP Efficacy in Ex Vivo - Whole Tissue" ? 
          {...obj, data: data.mrna_exp_ex_vivo_wt} : obj
      );
      mdcCopyData = mdcCopyData.map((obj) =>
        obj.name === "LNP Efficacy in Ex Vivo - Cell Type Specific" ? 
          {...obj, data: data.mrna_exp_ex_vivo_cts} : obj
      );

      const moiMaxReplicate = Math.max(
        ...data.mechanism_of_internalization.map(item => item.technical_replicate)
      );
      const moiMetricsHeading = replicateArray(
        mechanismOfInternalizationMetricsHeading, moiMaxReplicate
      );
      mdcCopyData = mdcCopyData.map((obj) =>
        obj.name === "Mechanism of Internalization" ? 
          {
            ...obj, 
            heading: mechanismOfInternalizationHeading.concat(moiMetricsHeading), 
            data: data.mechanism_of_internalization
          } 
          : obj
      );

      assayTabs = [...new Set(mdcCopyData.map((obj) => obj.tabName))];
      defaultAssayTab = assayTabs.length > 0 ? assayTabs[0] : '';
      setMdcTabData(mdcCopyData);
    }

    if (tabValue === 'UoL') {
      let uolCopyData = [...uolTabData];
      const dlsMaxReplicate = Math.max(
        ...data.dls.map(item => item.run)
      );
      const dlsMetricsHeading = replicateArray(
        dlsRunHeading, dlsMaxReplicate
      );
      uolCopyData = uolCopyData.map((obj) =>
        obj.name === "DLS" ? 
        {
          ...obj, 
          heading: dlsHeading.concat(dlsMetricsHeading),
          data: data.dls,
        } 
        : obj
      );
      uolCopyData = uolCopyData.map((obj) =>
        obj.name === "DLS Run" ? {...obj, data: data.dls_run_metrics} : obj
      );
      uolCopyData = uolCopyData.map((obj) =>
        obj.name === "LAL" ? {...obj, data: data.lal} : obj
      );
      uolCopyData = uolCopyData.map((obj) =>
        obj.name === "Cytotoxicity" ? {...obj, data: data.cytotoxicity} : obj
      );
      uolCopyData = uolCopyData.map((obj) =>
        obj.name === "KU812" ? {...obj, data: data.ku812} : obj
      );
      uolCopyData = uolCopyData.map((obj) =>
        obj.name === "Complement" ? {...obj, data: data.complement} : obj
      );

      const coagMaxReplicate = Math.max(...data.coagulation.map(item => item.replicate));
      const coagMetricsHeading = replicateArray(coagulationMetricsHeading, coagMaxReplicate);
      uolCopyData = uolCopyData.map((obj) =>
        obj.name === "Coagulation" ? 
          {
            ...obj, 
            data: data.coagulation, 
            heading: coagulationHeading.concat(coagMetricsHeading)
          } 
          : obj
      );

      assayTabs = [...new Set(uolCopyData.map((obj) => obj.tabName))];
      defaultAssayTab = assayTabs.length > 0 ? assayTabs[0] : '';
      setUolTabData(uolCopyData);
    }
    setAssayTabValue(defaultAssayTab);
    setAssayTabList(assayTabs);
  };

  const handleAssayTabChange = (e, tabValue) => {
    setAssayTabValue(tabValue);
  };

  useEffect(() => {
    let tableValues = [];
    if (tabValue === 'CPI') {
      tableValues = cpiTabData.filter(obj => obj.tabName === assayTabValue);
    } else if (tabValue === 'UoS') {
      tableValues = uosTabData.filter(obj => obj.tabName === assayTabValue);
    } else if (tabValue === 'MDC') {
      tableValues = mdcTabData.filter(obj => obj.tabName === assayTabValue);
    } else if (tabValue === 'UoL') {
      tableValues = uolTabData.filter(obj => obj.tabName === assayTabValue);
    }
    setAssayTableValues(tableValues);
    setShowNoData(!tableValues.some(item => item.data.length > 0));
  }, [assayTabValue, tabValue]);

  return(
    <AutoLogout>
      <SideDrawer drawerValue={drawerValue} setDrawerValue={setDrawerValue} />
      {apiRes.loading && <Loader />}
      <div className="mainContainer" style={{ marginLeft: drawerValue.drawerWidth }}>
        {apiRes.showAlert && <SuccessAlert apiRes={apiRes} setApiRes={setApiRes} />}
        <LNPSampleHeader 
          title={values.lnpSampleName} 
          backURL={backURL} 
          setShowCommentModal={setShowCommentModal} 
          handleLnpSampleDelete={handleLnpSampleDelete} 
          isDeleteBtnVisible={permisionsList.includes('Analytics-Manage Data')}
          isCommentBtnVisible={permisionsList.includes('Analytics-Manage Comment')}
        />

        <FormulationForm values={values} />

        {commentList.length > 0 && 
          <CommentCard 
            commentList={commentList} 
            handleDeleteComment={handleDeleteComment} 
          />
        }

        { values.parameterList.length > 0 &&
          <>
            <Typography 
              component="h5" 
              variant="h5" 
              color={'text.secondary'}
              sx={{ margin: 1.5 }}
            >
              <strong>LNP Parameters ({values.parameterList.length})</strong>
            </Typography>
            <SubTable heading={parameterHeading} rows={values.parameterList} />
          </>
        }

        { values.parameterIgniteList.length > 0 &&
          <>
            <Typography 
              component="h5" 
              variant="h5" 
              color={'text.secondary'}
              sx={{ margin: 1.5 }}
            >
              <strong>LNP Parameters Ignite ({values.parameterIgniteList.length})</strong>
            </Typography>
            <SubTable heading={parameterIgniteHeading} rows={values.parameterIgniteList} />
          </>
        }

        <Box sx={{ marginTop: 1.5, padding: 1 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab label='CPI' value='CPI' />
            <Tab label='UoS' value='UoS' />
            <Tab label='MDC' value='MDC' />
            <Tab label='UoL' value='UoL' />
          </Tabs>
        </Box>

        <Box sx={{ padding: 1 }}>
          <Tabs
            value={assayTabValue}
            onChange={handleAssayTabChange}
            textColor="secondary"
            indicatorColor="secondary"
          >
            {
              assayTabList.map((tab, index) => (
                <Tab key={index} label={tab} value={tab} />
              ))
            }
          </Tabs>
        </Box>

        <>
          {
            assayTableValues.map((data, index) => (
              <AnalysisTab key={index} data={data} />
            ))
          }

          {showNoData && <NoData />}
        </>

        <ErrorModal apiRes={apiRes} setApiRes={setApiRes} />

        <CommentModalForm 
          show={showCommentModal} 
          setShow={setShowCommentModal} 
          comment={comment} 
          setComment={setComment} 
          addComment={addComment} 
        />

        <DeleteModal
          title={deleteModalObj.title} 
          message={deleteModalObj.message} 
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          onDelete={
            deleteModalObj.type === 'LNP Sample' ? 
              deleteLNPSample : deleteComment
          }
        />
      </div>
    </AutoLogout>
  );
};

export default LNPSampleDetailPage;